import React, { useEffect, useMemo, useState } from "react";
import {
  FiBookOpen,
  FiFileText,
  FiImage,
  FiShield,
  FiShoppingBag,
  FiTarget,
  FiUser,
} from "react-icons/fi";
import { IconType } from "react-icons";
import styled from "styled-components";
import { getFallbackAppearance, getFallbackLabel, ImageVariant } from "../../utils/media";
import { FallbackImageSection } from "components/global/styles/FallbackImage.inline.styles";
type NativeImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src">;
interface FallbackImageProps extends NativeImageProps {
  src?: string | null;
  className?: string;
  imageClassName?: string;
  fallbackLabel?: string;
  variant?: ImageVariant;
}
const fallbackIconByVariant: Record<ImageVariant, IconType> = {
  athlete: FiUser,
  generic: FiImage,
  merchandise: FiShoppingBag,
  news: FiFileText,
  school: FiBookOpen,
  sport: FiTarget,
  team: FiShield,
  user: FiUser,
};
const FallbackToneSurface = styled.div<{
  $accentSoft: string;
}>`
  background: ${({ $accentSoft }) => $accentSoft};
`;
const FallbackInsetShape = styled.div`
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid transparent;
`;
const FallbackIconSurface = styled.div<{
  $accent: string;
}>`
  background: rgba(255, 255, 255, 0.88);
  box-shadow: none;
  color: ${({ $accent }) => $accent};
`;
const LoadingOverlay = styled.div`
  background: rgba(23, 37, 54, 0.12);
`;
export const FallbackImage: React.FC<FallbackImageProps> = ({
  alt,
  className = "",
  imageClassName = "",
  fallbackLabel,
  onError,
  onLoad,
  src,
  variant = "generic",
  ...imgProps
}) => {
  const safeSource = typeof src === "string" ? src.trim() : "";
  const accessibleLabel = useMemo(
    () =>
      getFallbackLabel({
        label: fallbackLabel || alt,
        variant,
      }),
    [alt, fallbackLabel, variant],
  );
  const appearance = getFallbackAppearance(variant);
  const FallbackIcon = fallbackIconByVariant[variant];
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(safeSource));
  useEffect(() => {
    setHasLoadError(false);
    setIsLoading(Boolean(safeSource));
  }, [safeSource]);
  if (!safeSource || hasLoadError) {
    return (
      <div
        aria-label={accessibleLabel}
        className={`relative shrink-0 overflow-hidden bg-[var(--brand-bg)] ${className}`.trim()}
        role="img"
      >
        <FallbackToneSurface
          aria-hidden="true"
          className="absolute inset-0"
          $accentSoft={appearance.accentSoft}
        />
        <FallbackInsetShape aria-hidden="true" className="absolute inset-[10%] rounded-[30%]" />
        <FallbackImageSection>
          <FallbackIconSurface
            aria-hidden="true"
            className="flex h-full w-full items-center justify-center rounded-[32%]"
            $accent={appearance.accent}
          >
            <FallbackIcon className="h-[52%] w-[52%]" data-testid="fallback-icon" />
          </FallbackIconSurface>
        </FallbackImageSection>
      </div>
    );
  }
  return (
    <div className={`relative shrink-0 overflow-hidden bg-[var(--brand-bg)] ${className}`.trim()}>
      {isLoading ? (
        <LoadingOverlay aria-hidden="true" className="absolute inset-0 animate-pulse" />
      ) : null}
      <img
        {...imgProps}
        alt={alt}
        className={`h-full w-full object-cover ${imageClassName}`.trim()}
        onError={(event) => {
          setHasLoadError(true);
          setIsLoading(false);
          onError?.(event);
        }}
        onLoad={(event) => {
          setIsLoading(false);
          onLoad?.(event);
        }}
        src={safeSource}
      />
    </div>
  );
};
export default FallbackImage;
